import { Chart } from 'chart.js';

const TOOLTIP_CLASS_NAME = 'main-analytics-chart-tooltip';

type ExternalTooltipContext = {
   chart: Chart;
   tooltip: {
      opacity: number;
      caretX: number;
      caretY: number;
      title?: string[];
      body?: { lines: string[] }[];
      labelColors?: { backgroundColor: unknown; borderColor: unknown }[];
   };
};

const getStyleColor = (value: unknown, fallback: string) =>
   typeof value === 'string' ? value : fallback;

const getOrCreateTooltip = (chart: Chart) => {
   const parent = chart.canvas.parentNode;

   if (!(parent instanceof HTMLElement)) {
      return null;
   }

   let tooltip = parent.querySelector<HTMLDivElement>(`.${TOOLTIP_CLASS_NAME}`);

   if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = TOOLTIP_CLASS_NAME;
      parent.appendChild(tooltip);
   }

   return tooltip;
};

export const renderChartTooltip = ({ chart, tooltip }: ExternalTooltipContext) => {
   const tooltipElement = getOrCreateTooltip(chart);

   if (!tooltipElement) {
      return;
   }

   if (tooltip.opacity === 0) {
      tooltipElement.style.opacity = '0';
      return;
   }

   const titleLines = tooltip.title ?? [];
   const bodyLines = tooltip.body?.flatMap((item) => item.lines) ?? [];
   const labelColors = tooltip.labelColors ?? [];

   tooltipElement.replaceChildren();

   titleLines.forEach((title) => {
      const titleElement = document.createElement('div');
      titleElement.textContent = title;
      titleElement.style.fontWeight = '600';
      titleElement.style.marginBottom = bodyLines.length ? '6px' : '0';
      tooltipElement.appendChild(titleElement);
   });

   bodyLines.forEach((line, index) => {
      const row = document.createElement('div');
      const marker = document.createElement('span');
      const text = document.createElement('span');
      const color = labelColors[index];
      const markerColor = getStyleColor(color?.backgroundColor, '#ffffff');

      row.style.alignItems = 'center';
      row.style.display = 'flex';
      row.style.gap = '8px';
      row.style.whiteSpace = 'nowrap';

      marker.style.background = markerColor;
      marker.style.border = `1px solid ${getStyleColor(color?.borderColor, markerColor)}`;
      marker.style.display = 'inline-block';
      marker.style.flex = '0 0 auto';
      marker.style.height = '10px';
      marker.style.width = '10px';

      text.textContent = line;

      row.append(marker, text);
      tooltipElement.appendChild(row);
   });

   tooltipElement.style.background = 'rgba(0, 0, 0, 0.8)';
   tooltipElement.style.borderRadius = '6px';
   tooltipElement.style.color = '#ffffff';
   tooltipElement.style.fontFamily = 'Arial, sans-serif';
   tooltipElement.style.fontSize = '16px';
   tooltipElement.style.left = `${chart.canvas.offsetLeft + tooltip.caretX}px`;
   tooltipElement.style.lineHeight = '1.25';
   tooltipElement.style.opacity = '1';
   tooltipElement.style.padding = '8px';
   tooltipElement.style.pointerEvents = 'none';
   tooltipElement.style.position = 'absolute';
   tooltipElement.style.top = `${chart.canvas.offsetTop + tooltip.caretY}px`;
   tooltipElement.style.transform = 'translate(-50%, calc(-100% - 8px))';
   tooltipElement.style.transition = 'opacity 80ms ease';
   tooltipElement.style.zIndex = '2';
};
